import { type TaggedEnumUnion, buildTaggedEnumType } from '@suntex.core/types';

import {
  fractionAdd,
  fractionDivide,
  fractionMultiply,
  fractionSimplify,
  fractionSubtract,
} from './fractions';
import type { Numeric } from './types';
import { asFraction, isFraction } from './types';

const $ = '_Operation';

enum _Operation {
  ADD = 1,
  SUB = 2,
  MUL = 3,
  DIV = 4,
  SQUR = 5,
  SQRT = 6,
  CUBE = 7,
  CBRT = 8,
}

export const Operation = buildTaggedEnumType($, _Operation);
export type Operation = TaggedEnumUnion<typeof Operation>;

export const { ADD, SUB, MUL, DIV, SQUR, SQRT, CUBE, CBRT } = Operation;

export function add(v1: Numeric, v2: Numeric): Numeric {
  if (isFraction(v1) || isFraction(v2)) {
    return fractionAdd(asFraction(v1), asFraction(v2));
  }
  return v1 + v2;
}

export function subtract(v1: Numeric, v2: Numeric): Numeric {
  if (isFraction(v1) || isFraction(v2)) {
    return fractionSubtract(asFraction(v1), asFraction(v2));
  }
  return v1 - v2;
}

export function multiply(v1: Numeric, v2: Numeric): Numeric {
  if (isFraction(v1) || isFraction(v2)) {
    return fractionMultiply(asFraction(v1), asFraction(v2));
  }
  return v1 * v2;
}

export function divide(v1: Numeric, v2: Numeric): Numeric | null {
  if (isFraction(v1) || isFraction(v2)) {
    return fractionDivide(asFraction(v1), asFraction(v2));
  }
  if (v2 === 0) return null;
  return v1 / v2;
}

export function square(value: Numeric): Numeric {
  if (isFraction(value)) return fractionMultiply(value, value);
  return value * value;
}

export function squareroot(value: Numeric): Numeric {
  if (isFraction(value)) {
    return fractionSimplify([Math.sqrt(value[0]), Math.sqrt(value[1])]);
  }
  return Math.sqrt(value);
}

const CUBE_EXPONENT = 3;

export function cube(value: Numeric): Numeric {
  if (isFraction(value))
    return fractionMultiply(value, fractionMultiply(value, value));
  return value ** CUBE_EXPONENT;
}

export function cuberoot(value: Numeric): Numeric {
  if (isFraction(value)) {
    return fractionSimplify([
      cuberoot(value[0]) as number,
      cuberoot(value[1]) as number,
    ]);
  }
  return Math.cbrt(value);
}

/**
 * Dispatch to the appropriate arithmetic function by op code.
 * Returns null for divide-by-zero.
 */
export function performOperation(
  val1: Numeric,
  op: number,
  val2: Numeric | null = null,
  _simplify = true,
): Numeric | null {
  switch (op) {
    case ADD:
      return add(val1, val2 ?? 0);
    case SUB:
      return subtract(val1, val2 ?? 0);
    case MUL:
      return multiply(val1, val2 ?? 0);
    case DIV:
      return divide(val1, val2 ?? 0);
    case SQUR:
      return square(val1);
    case SQRT:
      return squareroot(val1);
    case CUBE:
      return cube(val1);
    case CBRT:
      return cuberoot(val1);
    default:
      return null;
  }
}

export function getOpString(op: number): string {
  switch (op) {
    case ADD:
      return '+';
    case SUB:
      return '-';
    case MUL:
      return '\u00d7';
    case DIV:
      return '\u00f7';
    default:
      return '';
  }
}
